import React, { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Layout from './components/layout/Layout';
import CookieBanner from './components/layout/CookieBanner';
import SupportWidget from './components/SupportWidget';
import { useAuth } from './context/AuthContext';
import { PageLoader } from './components/ui';
import TrialBanner from './components/TrialBanner';
import lazyRetry from './lib/lazyRetry';
import { getNetwork, whenIdle } from './lib/network';

const Home = lazyRetry(() => import('./pages/Home'));
const About = lazyRetry(() => import('./pages/About'));
const News = lazyRetry(() => import('./pages/News'));
const Article = lazyRetry(() => import('./pages/Article'));
const Contact = lazyRetry(() => import('./pages/Contact'));
const Pricing = lazyRetry(() => import('./pages/Pricing'));
const Register = lazyRetry(() => import('./pages/auth/Register'));
const SignIn = lazyRetry(() => import('./pages/auth/SignIn'));
const ForgotPassword = lazyRetry(() => import('./pages/auth/ForgotPassword'));
const Welcome = lazyRetry(() => import('./pages/auth/Welcome'));
const Checkout = lazyRetry(() => import('./pages/billing/Checkout'));
const PaymentSuccess = lazyRetry(() => import('./pages/billing/PaymentSuccess'));
const PaymentCancelled = lazyRetry(() => import('./pages/billing/PaymentCancelled'));
const Account = lazyRetry(() => import('./pages/account/Account'));
const Library = lazyRetry(() => import('./features/library/LibraryPage'));
const Desktop = lazyRetry(() => import('./pages/Desktop'));
const Documentation = lazyRetry(() => import('./pages/Documentation'));
const Research = lazyRetry(() => import('./pages/Research'));
const MarketSurvey = lazyRetry(() => import('./pages/MarketSurvey'));
const Scout = lazyRetry(() => import('./pages/scout/ScoutPage'));
const Invest = lazyRetry(() => import('./pages/invest/InvestPage'));
const Privacy = lazyRetry(() => import('./pages/legal/Privacy'));
const Terms = lazyRetry(() => import('./pages/legal/Terms'));
const Cookies = lazyRetry(() => import('./pages/legal/Cookies'));
const LegalNotice = lazyRetry(() => import('./pages/legal/LegalNotice'));
const Unsubscribe = lazyRetry(() => import('./pages/Unsubscribe'));
const NotFound = lazyRetry(() => import('./pages/NotFound'));
const AdminMessages = lazyRetry(() => import('./pages/admin/AdminMessages'));

// Pages that need a full (non-guest) account.
function RequireMember({ children }) {
  const { loading, isMember, needsOnboarding } = useAuth();
  const location = useLocation();
  if (loading) return <PageLoader />;
  if (!isMember) return <Navigate to={`/sign-in?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (needsOnboarding && !['/welcome', '/account'].includes(location.pathname)) return <Navigate to={`/welcome?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return children;
}

// Signed-in users who haven't confirmed their age or the current terms are asked to, once.
function OnboardingGate({ children }) {
  const { needsOnboarding } = useAuth();
  const location = useLocation();
  // Legal pages, email links and account deletion must always stay reachable.
  const exempt = ['/welcome', '/privacy-policy', '/terms', '/cookies', '/legal-notice', '/account', '/sign-in', '/register', '/unsubscribe', '/contact-us'];
  if (needsOnboarding && !exempt.some((p) => location.pathname.startsWith(p))) {
    return <Navigate to={`/welcome?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }
  return children;
}

// On a fast connection, fetch the pages people most often open next while the browser is idle,
// so those clicks feel instant. Skipped on slow connections and when Data Saver is on.
function usePrefetchLikelyPages() {
  useEffect(() => whenIdle(() => {
    if (!getNetwork().fast) return;
    [Pricing, Library, Register, SignIn].forEach((c) => c.preload().catch(() => {}));
  }, 4000), []);
}

export default function App() {
  usePrefetchLikelyPages();
  return (
    <>
      <TrialBanner />
      <OnboardingGate>
        <Routes>
          <Route element={<Layout />}>
            <Route index element={<Home />} />
            <Route path="about" element={<About />} />
            <Route path="news" element={<News />} />
            <Route path="news/:articleSlug" element={<Article />} />
            <Route path="contact-us" element={<Contact />} />
            <Route path="pricing" element={<Pricing />} />
            <Route path="spectral-library" element={<Library />} />
            <Route path="spectral-library/:tab" element={<Library />} />
            <Route path="upload" element={<Navigate to="/spectral-library/upload" replace />} />
            <Route path="software/desktop-application" element={<Desktop />} />
            <Route path="documentation" element={<Documentation />} />
            <Route path="research-impact" element={<Research />} />
            <Route path="market-survey" element={<MarketSurvey />} />
            <Route path="analyser/SCOUT" element={<Scout />} />
            <Route path="analyser/scout" element={<Navigate to="/analyser/SCOUT" replace />} />
            <Route path="invest" element={<Invest />} />
            <Route path="privacy-policy" element={<Privacy />} />
            <Route path="terms" element={<Terms />} />
            <Route path="cookies" element={<Cookies />} />
            <Route path="legal-notice" element={<LegalNotice />} />
            <Route path="unsubscribe" element={<Unsubscribe />} />
            <Route path="payment-success" element={<PaymentSuccess />} />
            <Route path="payment-cancelled" element={<PaymentCancelled />} />
            <Route path="payment" element={<RequireMember><Checkout /></RequireMember>} />
            <Route path="account" element={<RequireMember><Account /></RequireMember>} />
            {/* Old addresses that appear in emails and external links */}
            <Route path="privacy" element={<Navigate to="/privacy-policy" replace />} />
            <Route path="legal" element={<Navigate to="/terms" replace />} />
            <Route path="research" element={<Navigate to="/research-impact" replace />} />
            <Route path="dashboard" element={<Navigate to="/account" replace />} />
            <Route path="faq" element={<Navigate to="/pricing#faq" replace />} />
            <Route path="admin" element={<Navigate to="/admin/messages" replace />} />
            <Route path="admin/messages" element={<AdminMessages />} />
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route element={<Layout bare />}>
            <Route path="register" element={<Register />} />
            <Route path="sign-in" element={<SignIn />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="welcome" element={<Welcome />} />
          </Route>
        </Routes>
      </OnboardingGate>
      <CookieBanner />
      <SupportWidget />
    </>
  );
}
